import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { BACKEND_ORIGIN } from "@/lib/api";
import { evenement } from "@/lib/pixel";
import { oublierConsentement } from "@/lib/consentement";
import { useI18n } from "@/i18n/I18nContext";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import LangSwitch from "@/components/LangSwitch";
import ThemeSwitch from "@/components/ThemeSwitch";
import Marque from "@/components/Marque";
import EcranPortable from "@/components/EcranPortable";
import { Button } from "@/components/ui/button";
import { ArrowRight, Check, Clock, Globe, MapPin, Phone, Timer } from "lucide-react";

// Le visiteur essaie le VRAI estimateur, pas une maquette. Ce jeton est celui
// du compte admin en mode « roofer » (voir backend/configurer_widget_roofer.py) :
// le visiteur est un couvreur, on lui montre la MESURE de son propre bâtiment et
// aucun prix — et il devient un prospect couvreur pour Roof Border.
const JETON_DEMO = "wid_roofer_roofborder";
const CONTACT = "quotes@roofborder.com";
// Numéro sans frais affiché : la version « vanity » se lit, le lien tel: compose
// les chiffres réels (4ROOF-21 = 476-6321).
const TEL_AFFICHE = "1-866-4ROOF-21";
const TEL_LIEN = "tel:+18664766321";

const T = {
  en: {
    connexion: "Sign in",
    tarifs: "Plans",
    surtitre: "Tools for roofing contractors",
    titre1: "One toolkit,",
    titre2: "built for roofers.",
    accroche:
      "From the one-truck operation to the full crew. Bring in leads, follow up without thinking about it, and send a quote in your own branding in under a minute. Our goal is simple: make your life easier.",
    essayer: "Try it on your own house",
    voirTarifs: "See the plans",
    legendeVideo: "Acquisition tools: done. Now we speed up!",
    legendeA: "Acquisition tools",
    legendeB: "Now we speed up!",
    statAncien: "the old way",
    statAncienV: "1–2 h",
    statAncienD: "Drive out, climb up, measure, calculate.",
    statNouveau: "with Roof Border",
    statNouveauV: "< 60 s",
    statNouveauD: "Address in, priced quote out.",
    statRoute: "trips to site",
    statRouteV: "0",
    statRouteD: "You only drive out once the job is worth it.",
    probTitre1: "Every quote that drags",
    probTitre2: "goes to the guy who called back first.",
    probTexte:
      "A homeowner shopping for a roof asks three contractors the same day. Between the drive, the measuring and the math, your price shows up second — and second rarely signs.",
    marcheSur: "How it works",
    marcheTitre: "From satellite image to priced quote.",
    etapes: [
      "You enter the customer's address.",
      "The satellite image is pulled.",
      "AI detects each roof and its pitch.",
      "Real surface is calculated in squares.",
      "The price is built from your rates.",
      "The PDF comes out branded as yours.",
    ],
    marcheNote:
      "The AI proposes, you decide: the outline stays editable by hand, and everything is yours to set — prices, materials, taxes, logo.",
    resSur: "What you get",
    resTitre: "Four tools that work together.",
    resultats: [
      {
        etiquette: "Lead generator",
        titre: "Your website starts producing requests",
        texte:
          "The estimator drops onto your site with two lines of code. Every curious visitor becomes a lead — with the roof already measured.",
        gain: "Requests that come to you",
      },
      {
        etiquette: "CRM",
        titre: "Every lead in one place",
        texte:
          "Statuses, notes, filters, bulk actions. You see at a glance who is waiting on you and who already signed.",
        gain: "Nothing slips through the cracks",
      },
      {
        etiquette: "Automated follow-up",
        titre: "The chasing happens without you",
        texte:
          "Up to five follow-up emails, on your schedule, in the customer's language. It stops on its own the moment they accept, decline or unsubscribe.",
        gain: "Follow-up keeps going while you are on a roof",
      },
      {
        etiquette: "Measure and quote",
        titre: "Address to PDF in under a minute",
        texte:
          "Satellite triangulation and AI measure the roof. Your materials, your prices, your margin do the rest. The quote comes out branded as yours, ready to send.",
        gain: "You answer first",
      },
    ],
    notes: [
      {
        titre: "Your data stays yours",
        texte: "Export your leads in one click into your own CRM or accounting system (CSV, Excel).",
      },
      {
        titre: "Your subscription can pay for itself",
        texte: "There is a way to wipe out your monthly bill. We walk you through it once you are inside.",
      },
      {
        titre: "And it keeps growing",
        texte: "We add tools continuously — and your suggestions often decide which one comes next.",
      },
    ],
    sysSur: "The rest of the system",
    sysTitre: "A missed call is a missed contract.",
    sysTexte:
      "The estimator works for the customer who already found you online. These two take care of the rest: the ones who call while you are up on a roof, and the ones who have not found you yet.",
    telTitre: "AI receptionist",
    telSous:
      "A voice that picks up on the first ring, at any hour, while you are on a roof with a nail gun in your hand.",
    telPoints: [
      "Answers 24/7 — nights, weekends, storm season.",
      "Speaks to callers in their own language.",
      "Never misses a call, never puts anyone on hold.",
      "Qualifies the caller and transfers the ones worth your time.",
      "You get the lead by text and email, with the recording.",
    ],
    telNote: "The same voice line already answering for other trades. Your hours, your prices, your words.",
    webTitre: "Roofer websites",
    webSous: "Built for roofing contractors and the people who hire them — not a generic template with a shingle photo.",
    webPoints: [
      "The satellite estimator built right into the page.",
      "Written to make one thing happen: a call or a request.",
      "Fast on a phone, in a driveway, on bad data.",
      "Local search: the neighbourhoods you actually serve.",
      "Bilingual when your market is.",
    ],
    webNote: "Sold on its own, or as the To the Roof package with the estimator and the receptionist.",
    sysBtn: "Talk to us about it",
    ecranSur: "A site we built",
    ecranTitre: "Scroll through a real roofer's website.",
    ecranTexte:
      "Not a screenshot — the actual site, live in the frame below. Scroll it, click it, open it on your phone.",
    ecranNote: "Prefer it full screen?",
    ecranLien: "Open the site in a new tab",
    demoSur: "Live demo — this is the real thing",
    demoTitre: "Put in an address you know.",
    demoTexte:
      "This is the same estimator your visitors would use, sitting on your website. Time it. And it works both ways: every address entered here reaches us as a lead, exactly like it would reach you.",
    demoNote: "No credit card, no sign-up. The estimate lands in your inbox.",
    ctaTitre: "Stop losing roofs to a slower quote.",
    ctaTexte: "Pick a plan and start pricing roofs today, or write us and we will measure a roof you already know.",
    ctaBtn: "See the plans",
    ctaMail: "Write to us",
    pied: "Satellite roof estimates for roofing contractors.",
  },
  fr: {
    connexion: "Connexion",
    tarifs: "Forfaits",
    surtitre: "Outils pour entrepreneurs en toiture",
    titre1: "Une suite d'outils",
    titre2: "faite pour les couvreurs.",
    accroche:
      "Du travailleur solo à la grosse équipe. Attirez des clients, suivez-les sans y penser, et sortez une soumission à votre image en moins d'une minute. Notre but est simple : vous rendre la vie plus facile.",
    essayer: "Essayez sur votre maison",
    voirTarifs: "Voir les forfaits",
    legendeVideo: "Outils d'acquisition : fait. Maintenant on accélère !",
    legendeA: "Outils d'acquisition",
    legendeB: "Maintenant on accélère !",
    statAncien: "à l'ancienne",
    statAncienV: "1–2 h",
    statAncienD: "Se déplacer, monter, mesurer, calculer.",
    statNouveau: "avec Roof Border",
    statNouveauV: "< 60 s",
    statNouveauD: "L'adresse entre, le prix sort.",
    statRoute: "déplacement",
    statRouteV: "0",
    statRouteD: "Vous sortez seulement quand le contrat en vaut la peine.",
    probTitre1: "Chaque soumission qui traîne",
    probTitre2: "part chez le concurrent qui a rappelé avant vous.",
    probTexte:
      "Un client qui magasine une toiture demande trois prix le même jour. Entre le déplacement, les mesures et le calcul, votre prix arrive deuxième — et le deuxième ne signe pas souvent.",
    marcheSur: "Comment ça marche",
    marcheTitre: "De l'image satellite à la soumission chiffrée.",
    etapes: [
      "Vous entrez l'adresse du client.",
      "L'image satellite est récupérée.",
      "L'IA détecte chaque toit et sa pente.",
      "La surface réelle est calculée en carrés.",
      "Le prix est établi selon vos taux.",
      "Le PDF sort à votre image.",
    ],
    marcheNote:
      "L'IA propose, vous disposez : le contour reste modifiable à la main, et tout est personnalisable — prix, matériaux, taxes, logo.",
    resSur: "Ce que vous obtenez",
    resTitre: "Quatre outils qui travaillent ensemble.",
    resultats: [
      {
        etiquette: "Générateur de prospects",
        titre: "Votre site devient une machine à demandes",
        texte:
          "L'estimateur s'installe sur votre site en deux lignes de code. Chaque visiteur curieux devient un prospect — avec son toit déjà mesuré.",
        gain: "Des demandes qui arrivent toutes seules",
      },
      {
        etiquette: "CRM",
        titre: "Tous vos prospects au même endroit",
        texte:
          "Statuts, notes, filtres, actions groupées. Vous voyez d'un coup d'œil qui attend une réponse et qui a déjà signé.",
        gain: "Plus rien ne tombe entre deux chaises",
      },
      {
        etiquette: "Relance automatique",
        titre: "Le suivi se fait sans vous",
        texte:
          "Jusqu'à cinq courriels de relance, à vos délais, dans la langue du client. Ça s'arrête tout seul dès qu'il accepte, refuse ou se désabonne.",
        gain: "Le suivi continue pendant que vous êtes sur le toit",
      },
      {
        etiquette: "Mesure et soumission",
        titre: "De l'adresse au PDF en moins d'une minute",
        texte:
          "La triangulation satellite et l'IA mesurent le toit. Vos matériaux, vos prix, votre marge font le reste. La soumission sort à votre image, prête à envoyer.",
        gain: "Vous répondez le premier",
      },
    ],
    notes: [
      {
        titre: "Vos données restent les vôtres",
        texte:
          "Exportez vos prospects en un clic vers votre propre CRM ou votre système comptable (CSV, Excel).",
      },
      {
        titre: "Votre abonnement peut se payer tout seul",
        texte: "Il existe une façon d'effacer votre facture mensuelle. On vous l'explique une fois à l'intérieur.",
      },
      {
        titre: "Et ça continue de grandir",
        texte:
          "On ajoute des outils en continu — et vos suggestions décident souvent du prochain.",
      },
    ],
    sysSur: "Le reste du système",
    sysTitre: "Un appel manqué, c'est un contrat manqué.",
    sysTexte:
      "L'estimateur travaille pour le client qui vous a déjà trouvé en ligne. Ces deux-là s'occupent du reste : ceux qui appellent pendant que vous êtes sur un toit, et ceux qui ne vous ont pas encore trouvé.",
    telTitre: "Téléphoniste IA",
    telSous:
      "Une voix qui décroche à la première sonnerie, à n'importe quelle heure, pendant que vous êtes sur un toit, le cloueur à la main.",
    telPoints: [
      "Répond 24 h sur 24, 7 jours sur 7 — soirs, fins de semaine, saison des tempêtes.",
      "Parle à l'appelant dans sa langue.",
      "Ne rate aucun appel, ne met personne en attente.",
      "Qualifie l'appelant et vous transfère ceux qui valent votre temps.",
      "Le prospect vous arrive par texto et par courriel, avec l'enregistrement.",
    ],
    telNote:
      "La même ligne vocale qui répond déjà pour d'autres corps de métier. Vos heures, vos prix, vos mots.",
    webTitre: "Sites web de couvreur",
    webSous:
      "Conçus pour les entrepreneurs en toiture et pour les gens qui les engagent — pas un gabarit générique avec une photo de bardeau.",
    webPoints: [
      "L'estimateur satellite intégré à même la page.",
      "Écrits pour provoquer une seule chose : un appel ou une demande.",
      "Rapides sur un cellulaire, dans une entrée de garage, sur un mauvais réseau.",
      "Référencement local : les quartiers que vous desservez vraiment.",
      "Bilingues quand votre marché l'est.",
    ],
    webNote:
      "Vendus seuls, ou dans le forfait To the Roof avec l'estimateur et la téléphoniste.",
    sysBtn: "En parler avec nous",
    ecranSur: "Un site que nous avons bâti",
    ecranTitre: "Parcourez un vrai site de couvreur.",
    ecranTexte:
      "Ce n'est pas une capture d'écran — c'est le site lui-même, vivant dans le cadre ci-dessous. Faites-le défiler, cliquez dedans, ouvrez-le sur votre cellulaire.",
    ecranNote: "Vous le préférez en plein écran ?",
    ecranLien: "Ouvrir le site dans un nouvel onglet",
    demoSur: "Démonstration en direct — c'est le vrai outil",
    demoTitre: "Entrez une adresse que vous connaissez.",
    demoTexte:
      "C'est exactement l'estimateur que vos visiteurs utiliseraient sur votre site web. Chronométrez-le. Et il fonctionne dans les deux sens : chaque adresse entrée ici nous arrive comme un prospect, exactement comme elle vous arriverait.",
    demoNote: "Sans carte de crédit, sans inscription. L'estimation arrive dans votre boîte.",
    ctaTitre: "Arrêtez de perdre des toitures à cause d'une soumission trop lente.",
    ctaTexte:
      "Choisissez un forfait et chiffrez vos toitures dès aujourd'hui, ou écrivez-nous et on mesure devant vous un toit que vous connaissez déjà.",
    ctaBtn: "Voir les forfaits",
    ctaMail: "Écrivez-nous",
    pied: "Estimation de toiture par satellite, pour les entrepreneurs en toiture.",
  },
  es: {
    connexion: "Iniciar sesión",
    tarifs: "Planes",
    surtitre: "Herramientas para contratistas de techos",
    titre1: "Un conjunto de herramientas",
    titre2: "hecho para techadores.",
    accroche:
      "Del que trabaja solo al equipo completo. Atraiga clientes, deles seguimiento sin pensarlo, y envíe una cotización con su marca en menos de un minuto. Nuestro objetivo es simple: hacerle la vida más fácil.",
    essayer: "Pruébelo con su propia casa",
    voirTarifs: "Ver los planes",
    legendeVideo: "Herramientas de captación: listo. ¡Ahora aceleramos!",
    legendeA: "Herramientas de captación",
    legendeB: "¡Ahora aceleramos!",
    statAncien: "a la antigua",
    statAncienV: "1–2 h",
    statAncienD: "Conducir, subir, medir, calcular.",
    statNouveau: "con Roof Border",
    statNouveauV: "< 60 s",
    statNouveauD: "Entra la dirección, sale la cotización con precio.",
    statRoute: "viajes al sitio",
    statRouteV: "0",
    statRouteD: "Usted solo se desplaza cuando el trabajo vale la pena.",
    probTitre1: "Cada cotización que se demora",
    probTitre2: "se la lleva el que devolvió la llamada primero.",
    probTexte:
      "Un propietario que busca techo pide precio a tres contratistas el mismo día. Entre el viaje, las mediciones y el cálculo, su precio llega segundo — y el segundo rara vez firma.",
    marcheSur: "Cómo funciona",
    marcheTitre: "De la imagen satelital a la cotización con precio.",
    etapes: [
      "Usted ingresa la dirección del cliente.",
      "Se obtiene la imagen satelital.",
      "La IA detecta cada techo y su pendiente.",
      "La superficie real se calcula en squares.",
      "El precio se arma con sus tarifas.",
      "El PDF sale con su propia marca.",
    ],
    marcheNote:
      "La IA propone, usted decide: el contorno sigue siendo editable a mano, y todo se configura a su gusto — precios, materiales, impuestos, logotipo.",
    resSur: "Lo que usted obtiene",
    resTitre: "Cuatro herramientas que trabajan juntas.",
    resultats: [
      {
        etiquette: "Generador de prospectos",
        titre: "Su sitio web empieza a producir solicitudes",
        texte:
          "El estimador se instala en su sitio con dos líneas de código. Cada visitante curioso se convierte en prospecto — con el techo ya medido.",
        gain: "Solicitudes que llegan solas",
      },
      {
        etiquette: "CRM",
        titre: "Todos sus prospectos en un solo lugar",
        texte:
          "Estados, notas, filtros, acciones en lote. Ve de un vistazo quién espera respuesta y quién ya firmó.",
        gain: "Nada se pierde en el camino",
      },
      {
        etiquette: "Seguimiento automático",
        titre: "El seguimiento se hace sin usted",
        texte:
          "Hasta cinco correos de seguimiento, con sus tiempos, en el idioma del cliente. Se detiene solo en cuanto el cliente acepta, rechaza o se da de baja.",
        gain: "El seguimiento sigue mientras usted está en un techo",
      },
      {
        etiquette: "Medición y cotización",
        titre: "De la dirección al PDF en menos de un minuto",
        texte:
          "La triangulación satelital y la IA miden el techo. Sus materiales, sus precios, su margen hacen el resto. La cotización sale con su marca, lista para enviar.",
        gain: "Usted responde primero",
      },
    ],
    notes: [
      {
        titre: "Sus datos siguen siendo suyos",
        texte: "Exporte sus prospectos con un clic a su propio CRM o sistema contable (CSV, Excel).",
      },
      {
        titre: "Su suscripción puede pagarse sola",
        texte: "Existe una forma de borrar su factura mensual. Se lo explicamos una vez adentro.",
      },
      {
        titre: "Y sigue creciendo",
        texte: "Agregamos herramientas continuamente — y sus sugerencias deciden a menudo cuál sigue.",
      },
    ],
    sysSur: "El resto del sistema",
    sysTitre: "Una llamada perdida es un contrato perdido.",
    sysTexte:
      "El estimador trabaja para el cliente que ya lo encontró en línea. Estos dos se ocupan del resto: los que llaman mientras usted está en un techo, y los que todavía no lo han encontrado.",
    telTitre: "Recepcionista IA",
    telSous:
      "Una voz que contesta al primer timbre, a cualquier hora, mientras usted está en un techo con la clavadora en la mano.",
    telPoints: [
      "Responde 24/7 — noches, fines de semana, temporada de tormentas.",
      "Le habla a quien llama en su propio idioma.",
      "Nunca pierde una llamada, nunca deja a nadie en espera.",
      "Califica a quien llama y le transfiere a los que valen su tiempo.",
      "Usted recibe el prospecto por mensaje de texto y correo, con la grabación.",
    ],
    telNote: "La misma línea de voz que ya responde para otros oficios. Sus horarios, sus precios, sus palabras.",
    webTitre: "Sitios web para techadores",
    webSous:
      "Hechos para contratistas de techos y para la gente que los contrata — no una plantilla genérica con una foto de tejas.",
    webPoints: [
      "El estimador satelital integrado directamente en la página.",
      "Escritos para provocar una sola cosa: una llamada o una solicitud.",
      "Rápidos en un celular, en una entrada de auto, con mala señal.",
      "Búsqueda local: los vecindarios que usted realmente atiende.",
      "Bilingües cuando su mercado lo es.",
    ],
    webNote: "Se venden solos, o en el paquete To the Roof con el estimador y la recepcionista.",
    sysBtn: "Hablemos de esto",
    ecranSur: "Un sitio que construimos",
    ecranTitre: "Recorra el sitio web de un techador real.",
    ecranTexte:
      "No es una captura de pantalla — es el sitio mismo, vivo en el marco de abajo. Deslícelo, haga clic, ábralo en su teléfono.",
    ecranNote: "¿Lo prefiere en pantalla completa?",
    ecranLien: "Abrir el sitio en una pestaña nueva",
    demoSur: "Demostración en vivo — es la herramienta real",
    demoTitre: "Ingrese una dirección que conozca.",
    demoTexte:
      "Es el mismo estimador que usarían sus visitantes, instalado en su sitio web. Tómele el tiempo. Y funciona en ambos sentidos: cada dirección ingresada aquí nos llega como prospecto, exactamente como le llegaría a usted.",
    demoNote: "Sin tarjeta de crédito, sin registro. La estimación llega a su bandeja de entrada.",
    ctaTitre: "Deje de perder techos por una cotización más lenta.",
    ctaTexte:
      "Elija un plan y empiece a cotizar techos hoy mismo, o escríbanos y mediremos un techo que usted ya conoce.",
    ctaBtn: "Ver los planes",
    ctaMail: "Escríbanos",
    pied: "Estimaciones de techos por satélite para contratistas de techos.",
  },
};

/** Estimateur public reel, monte dans la page vitrine. */
function EstimateurDemo({ lang, theme }) {
  const conteneur = useRef(null);

  // Un couvreur qui fait mesurer un toit sur la page d'accueil, c'est LA
  // conversion des campagnes publicitaires : il a essayé l'outil et il est
  // devenu un prospect. Le widget émet le signal, on le traduit en Lead.
  useEffect(() => {
    const surEstimation = (e) => {
      evenement("Lead", {
        content_name: e.detail?.roofer ? "estimateur_roofer" : "estimateur_client",
        content_category: e.detail?.mesure ? "mesure" : "demande",
      });
    };
    window.addEventListener("roofborder:estimation", surEstimation);
    return () => window.removeEventListener("roofborder:estimation", surEstimation);
  }, []);

  useEffect(() => {
    const boite = conteneur.current;
    if (!boite) return;
    // Le script du widget cherche #estimateur-toiture au chargement. On le
    // reinsere a chaque changement de langue ou de theme : reinserer une balise
    // <script> la fait reexecuter, meme servie depuis le cache.
    boite.innerHTML = "";
    const script = document.createElement("script");
    script.src = `${BACKEND_ORIGIN}/api/widget.js`;
    script.async = true;
    document.body.appendChild(script);
    return () => {
      script.remove();
      if (boite) boite.innerHTML = "";
    };
  }, [lang, theme]);

  return (
    <div
      id="estimateur-toiture"
      ref={conteneur}
      data-token={JETON_DEMO}
      data-lang={lang}
      data-theme={theme === "clair" ? "clair" : "sombre"}
    />
  );
}

export default function Accueil() {
  const { lang } = useI18n();
  const { theme } = useTheme();
  const { user } = useAuth();
  const L = T[lang] || T.en;

  // Réglage système « réduire les animations » : lu une fois au rendu.
  const mouvementReduit =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const versDemo = (e) => {
    e.preventDefault();
    document.getElementById("demo")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-app text-ink">
      <header className="border-b border-line sticky top-0 z-30 bg-app/90 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Marque taille="md" />
          <div className="flex items-center gap-3">
            <a href={TEL_LIEN} className="hidden md:inline-flex items-center gap-1.5 text-sm font-bold text-brand-ink hover:text-brand transition-colors">
              <Phone className="h-4 w-4" /> {TEL_AFFICHE}
            </a>
            <ThemeSwitch className="hidden sm:inline-flex" />
            <LangSwitch langues={["en", "fr", "es"]} />
            <Link to="/tarifs" className="hidden sm:block text-sm text-ink-soft hover:text-ink transition-colors">
              {L.tarifs}
            </Link>
            <Link to={user ? "/dashboard" : "/login"}>
              <Button className="bg-brand hover:bg-brand/90 text-white rounded-md h-9 font-bold">
                {user ? "Dashboard" : L.connexion}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* ---------------- hero ---------------- */}
      <section className="max-w-6xl mx-auto px-6 pt-14 pb-16 grid lg:grid-cols-[1fr_1.25fr] gap-10 items-center">
        <div>
          <p className="text-[11px] font-extrabold uppercase tracking-[0.28em] text-brand-ink mb-5">
            {L.surtitre}
          </p>
          <h1 className="font-heading font-extrabold leading-[0.92] tracking-tighter text-[clamp(2.6rem,6.4vw,4.75rem)]">
            {L.titre1}
            <br />
            <span className="text-brand">{L.titre2}</span>
          </h1>
          <p className="mt-6 text-lg text-ink-soft max-w-xl leading-relaxed">{L.accroche}</p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <a href="#demo" onClick={versDemo}>
              <Button className="bg-brand hover:bg-brand/90 text-white rounded-md h-12 px-7 text-base font-bold">
                {L.essayer}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </a>
            <Link to="/tarifs">
              <Button
                variant="outline"
                className="border-line-strong rounded-md h-12 px-6 text-base hover:bg-hover-strong hover:text-ink"
              >
                {L.voirTarifs}
              </Button>
            </Link>
          </div>
        </div>

        <figure className="m-0">
          <div className="relative rounded-xl overflow-hidden border border-line shadow-releve bg-black">
            {/* Qui a demandé moins d'animations reçoit l'image fixe — et évite
                au passage de télécharger la vidéo. */}
            {mouvementReduit ? (
              <img
                src={`${process.env.PUBLIC_URL}/toiture-rapide.jpg`}
                alt={L.legendeVideo}
                className="w-full block"
              />
            ) : (
              <video
                className="w-full block"
                src={`${process.env.PUBLIC_URL}/toiture-rapide.mp4`}
                poster={`${process.env.PUBLIC_URL}/toiture-rapide.jpg`}
                autoPlay
                muted
                loop
                playsInline
                aria-label={L.legendeVideo}
              />
            )}
            <figcaption className="absolute left-0 right-0 bottom-0 p-4 pt-10 bg-gradient-to-t from-black/85 to-transparent">
              {/* Le crochet est une icône, pas un caractère : il garde son poids
                  visuel quelle que soit la police du système. */}
              <span className="font-heading text-white font-bold text-lg sm:text-xl tracking-tight inline-flex flex-wrap items-center gap-x-2 gap-y-1">
                <span>{L.legendeA}</span>
                <Check className="h-5 w-5 sm:h-6 sm:w-6 text-brand shrink-0" strokeWidth={3.5} />
                <span>{L.legendeB}</span>
              </span>
            </figcaption>
          </div>
        </figure>
      </section>

      {/* ---------------- chiffres ---------------- */}
      <section className="border-y border-line bg-surface">
        <div className="max-w-6xl mx-auto px-6 grid sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-line">
          {[
            [Clock, L.statAncienV, L.statAncien, L.statAncienD, false],
            [Timer, L.statNouveauV, L.statNouveau, L.statNouveauD, true],
            [MapPin, L.statRouteV, L.statRoute, L.statRouteD, false],
          ].map(([Icone, valeur, etiquette, detail, fort], i) => (
            <div key={i} className="py-8 sm:px-8 first:sm:pl-0 last:sm:pr-0">
              <Icone className={`h-5 w-5 mb-3 ${fort ? "text-brand" : "text-ink-faint"}`} />
              <div
                className={`font-heading font-extrabold tracking-tighter text-4xl ${
                  fort ? "text-brand" : "text-ink"
                }`}
              >
                {valeur}
              </div>
              <div className="text-xs font-bold uppercase tracking-widest text-ink-faint mt-1">{etiquette}</div>
              <p className="text-sm text-ink-soft mt-2">{detail}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ---------------- le probleme ---------------- */}
      <section className="max-w-4xl mx-auto px-6 py-20">
        <h2 className="font-heading font-extrabold tracking-tighter leading-[1.02] text-[clamp(1.9rem,4.2vw,3rem)]">
          {L.probTitre1}
          <br />
          <span className="text-brand">{L.probTitre2}</span>
        </h2>
        <p className="mt-6 text-lg text-ink-soft leading-relaxed">{L.probTexte}</p>
      </section>

      {/* ---------------- comment ca marche ---------------- */}
      <section className="border-t border-line bg-surface">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <p className="text-[11px] font-extrabold uppercase tracking-[0.28em] text-brand-ink mb-4">
            {L.marcheSur}
          </p>
          <h2 className="font-heading font-extrabold tracking-tighter text-[clamp(1.8rem,3.6vw,2.6rem)] max-w-2xl">
            {L.marcheTitre}
          </h2>
          <ol className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-8 list-none p-0">
            {L.etapes.map((etape, i) => (
              <li key={i} className="flex gap-4 items-start">
                <span className="font-heading font-extrabold text-3xl tracking-tighter text-brand/35 leading-none w-10 shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-ink-soft pt-1">{etape}</span>
              </li>
            ))}
          </ol>
          <p className="mt-10 text-sm text-ink-faint border-l-2 border-brand pl-4 max-w-2xl">{L.marcheNote}</p>
        </div>
      </section>

      {/* ---------------- trois resultats ---------------- */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <p className="text-[11px] font-extrabold uppercase tracking-[0.28em] text-brand-ink mb-4">{L.resSur}</p>
        <h2 className="font-heading font-extrabold tracking-tighter text-[clamp(1.8rem,3.6vw,2.6rem)] max-w-2xl mb-9">
          {L.resTitre}
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          {L.resultats.map((r, i) => (
            <div
              key={i}
              className="border-l-4 border-brand bg-surface rounded-r-xl shadow-carte p-6 flex flex-col"
            >
              <div className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-ink-faint">
                {String(i + 1).padStart(2, "0")} — {r.etiquette}
              </div>
              <h3 className="font-heading font-bold text-xl tracking-tight mt-2">{r.titre}</h3>
              <p className="text-ink-soft text-sm mt-3 flex-1">{r.texte}</p>
              <p className="text-brand-ink font-bold text-sm mt-5">{r.gain}</p>
            </div>
          ))}
        </div>

        {/* Trois promesses de fond. Celle du milieu reste volontairement vague :
            la mécanique de parrainage ne se raconte pas à la concurrence. */}
        <div className="mt-6 grid sm:grid-cols-3 gap-6 border-t border-line pt-8">
          {L.notes.map((n, i) => (
            <div key={i}>
              <h3 className="font-heading font-bold text-base tracking-tight">{n.titre}</h3>
              <p className="text-ink-soft text-sm mt-2">{n.texte}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ---------------- demo en direct ---------------- */}
      <section id="demo" className="border-y border-line bg-surface scroll-mt-16">
        <div className="max-w-6xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <p className="text-[11px] font-extrabold uppercase tracking-[0.28em] text-brand-ink mb-4">
              {L.demoSur}
            </p>
            <h2 className="font-heading font-extrabold tracking-tighter text-[clamp(1.9rem,4vw,2.9rem)] leading-[1.02]">
              {L.demoTitre}
            </h2>
            <p className="mt-6 text-lg text-ink-soft leading-relaxed">{L.demoTexte}</p>
            <p className="mt-4 text-sm text-ink-faint">{L.demoNote}</p>
          </div>
          <div className="bg-app border border-line rounded-xl shadow-releve p-5">
            <EstimateurDemo lang={T[lang] ? lang : "en"} theme={theme} />
          </div>
        </div>
      </section>

      {/* ---------------- le reste du systeme ---------------- */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <p className="text-[11px] font-extrabold uppercase tracking-[0.28em] text-brand-ink mb-4">{L.sysSur}</p>
        <h2 className="font-heading font-extrabold tracking-tighter text-[clamp(1.9rem,4.2vw,2.9rem)] leading-[1.02] max-w-3xl">
          {L.sysTitre}
        </h2>
        <p className="mt-5 text-lg text-ink-soft max-w-2xl leading-relaxed">{L.sysTexte}</p>

        <div className="mt-10 grid lg:grid-cols-2 gap-6">
          {[
            [Phone, L.telTitre, L.telSous, L.telPoints, L.telNote, true],
            [Globe, L.webTitre, L.webSous, L.webPoints, L.webNote, false],
          ].map(([Icone, titre, sous, points, note, vedette], i) => (
            <div
              key={i}
              className={`rounded-xl p-7 flex flex-col border shadow-carte ${
                vedette ? "border-brand/50 bg-brand/[0.06]" : "border-line bg-surface"
              }`}
            >
              <Icone className="h-6 w-6 text-brand mb-4" />
              <h3 className="font-heading font-extrabold text-2xl tracking-tight">{titre}</h3>
              <p className="text-ink-soft mt-3">{sous}</p>
              <ul className="mt-6 space-y-3 text-sm flex-1">
                {points.map((p, j) => (
                  <li key={j} className="flex gap-3">
                    <Check className="h-4 w-4 text-brand-ink shrink-0 mt-0.5" />
                    <span className="text-ink-soft">{p}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-ink-faint mt-6 border-l-2 border-line-strong pl-3">{note}</p>
              <a
                href={`mailto:${CONTACT}?subject=${encodeURIComponent(`${titre} — Roof Border`)}`}
                className="mt-6"
              >
                <Button
                  className={`w-full rounded-md font-bold h-11 ${
                    vedette
                      ? "bg-brand hover:bg-brand/90 text-white"
                      : "bg-hover-strong hover:bg-line-strong text-ink"
                  }`}
                >
                  {L.sysBtn}
                </Button>
              </a>
            </div>
          ))}
        </div>
      </section>

      {/* ---------------- site de couvreur, dans un écran ---------------- */}
      <section className="max-w-6xl mx-auto px-6 pb-20">
        <div className="text-center mb-8">
          <p className="text-[11px] font-extrabold uppercase tracking-[0.28em] text-brand-ink mb-3">
            {L.ecranSur}
          </p>
          <h3 className="font-heading font-extrabold tracking-tighter text-[clamp(1.5rem,3vw,2.2rem)]">
            {L.ecranTitre}
          </h3>
          <p className="text-ink-soft mt-3 max-w-xl mx-auto">{L.ecranTexte}</p>
        </div>

        <figure className="m-0">
          <EcranPortable
            src={`${process.env.PUBLIC_URL}/demo-roofer/`}
            titre={L.ecranTitre}
            className="max-w-4xl"
          />
        </figure>

        <p className="text-center text-sm text-ink-faint mt-6">
          {L.ecranNote}{" "}
          <a
            href={`${process.env.PUBLIC_URL}/demo-roofer/`}
            target="_blank"
            rel="noreferrer"
            className="text-brand-ink hover:underline"
          >
            {L.ecranLien}
          </a>
        </p>
      </section>

      {/* ---------------- appel a l'action ---------------- */}
      <section className="max-w-4xl mx-auto px-6 py-24 text-center">
        <h2 className="font-heading font-extrabold tracking-tighter text-[clamp(1.9rem,4.4vw,3rem)] leading-[1.02]">
          {L.ctaTitre}
        </h2>
        <p className="mt-6 text-lg text-ink-soft max-w-2xl mx-auto">{L.ctaTexte}</p>
        <div className="mt-9 flex flex-wrap justify-center gap-3">
          <Link to="/tarifs">
            <Button className="bg-brand hover:bg-brand/90 text-white rounded-md h-12 px-8 text-base font-bold">
              {L.ctaBtn}
            </Button>
          </Link>
          <a href={`mailto:${CONTACT}`}>
            <Button
              variant="outline"
              className="border-line-strong rounded-md h-12 px-6 text-base hover:bg-hover-strong hover:text-ink"
            >
              {L.ctaMail}
            </Button>
          </a>
        </div>
      </section>

      <footer className="border-t border-line">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-wrap items-center justify-between gap-4">
          <div>
            <Marque taille="sm" />
            <p className="text-xs text-ink-faint mt-2">{L.pied}</p>
          </div>
          <div className="flex flex-col items-start sm:items-end gap-1">
            <a href={TEL_LIEN} className="inline-flex items-center gap-1.5 text-sm font-bold text-brand-ink hover:underline">
              <Phone className="h-4 w-4" /> {TEL_AFFICHE}
            </a>
            <a href={`mailto:${CONTACT}`} className="text-sm text-brand-ink hover:underline">
              {CONTACT}
            </a>
            <div className="flex items-center gap-3 mt-1 text-xs text-ink-faint">
              <Link to="/confidentialite" className="hover:text-ink transition-colors">
                {lang === "fr" ? "Confidentialité" : lang === "es" ? "Privacidad" : "Privacy"}
              </Link>
              <span>·</span>
              <Link to="/conditions" className="hover:text-ink transition-colors">
                {lang === "fr" ? "Conditions" : lang === "es" ? "Términos" : "Terms"}
              </Link>
              <span>·</span>
              {/* Se retirer doit être aussi simple que consentir : ce lien
                  repose la question, d'où qu'on soit sur le site. */}
              <button
                type="button"
                onClick={oublierConsentement}
                data-testid="pied-temoins"
                className="hover:text-ink transition-colors"
              >
                {lang === "fr" ? "Témoins" : lang === "es" ? "Cookies" : "Cookies"}
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
